import TomSelect from 'tom-select';
import * as Popper from '@popperjs/core';
import {escapeHtml, getDeviceType, isMatchMedia, isWindowsEdge} from '%helpers%/utils';
import intlTelInput from 'intl-tel-input';
import Inputmask from 'inputmask';
import {INPUTMASK} from '%helpers%/constants';
import noUiSlider from 'nouislider';
import wNumb from 'wnumb';

//== DropdownSelect

export const selectDropdownItem = dropdownItem => {
  if (!dropdownItem) return;
  const dropdown = dropdownItem.closest('.dropdown');
  const dropdownToggle = dropdown.querySelector('.dropdown-toggle');
  const targetSelector = dropdown.dataset.targetValue || null;
  const targetElement = targetSelector ? dropdownItem.querySelector(targetSelector) : null;
  const textDropdownItem = targetElement ? targetElement.innerHTML : dropdownItem.innerHTML;

  const dropdownItemSiblings = [...dropdownItem.parentElement.children].filter(
    child => child !== dropdownItem
  );
  dropdownItem.classList.add('active');
  dropdownItemSiblings.forEach(el => el.classList.remove('active'));

  dropdownToggle.querySelector('.text').innerHTML = textDropdownItem;
};

export const initDropdownSelect = el => {
  const toggle = el.querySelector('.dropdown-toggle');
  const dropdownItem = el.querySelector('.dropdown-item.active') || el.querySelector('.dropdown-item')[0];

  const {hideSelected = false, locked = false} = toggle.dataset;

  if (!locked) {
    selectDropdownItem(dropdownItem);

    el.querySelectorAll('.dropdown-item').forEach(el => {
      el.addEventListener('click', () => {
        selectDropdownItem(el);
      });
    });
  }

  if (hideSelected) {
    el.classList.add('is-hide-selected');
  }
};

//== Inputmask

export const initInputMaskByKey = (el, key) => {
  if (!el || !INPUTMASK.hasOwnProperty(key)) return;
  initInputMask(el, {...INPUTMASK[key]});
};

export const initInputMask = (input, options = {}) => {
  // Inputmask blocks text entry on Edge/Windows when OS text suggestions are on.
  // Skip masking there rather than lock users out of the field.
  if (!input || isWindowsEdge()) return;

  const defaultSettings = {
    showMaskOnFocus: false,
    showMaskOnHover: false,
    showMaskOnBlur: false,
    placeholder: '',
  };

  const {maskPrefix} = input.dataset;

  const settings = {
    ...defaultSettings,
    ...options,
    prefix: maskPrefix,
  };

  const maskInstance = Inputmask(settings);

  maskInstance.mask(input);

  return maskInstance;
};

// Helper line under the cash-out field on equity-estimator step 2. The 90%
// loan-to-value check still decides whether they can borrow, but the line
// never shows that dollar figure.
export const EQUITY_SHORTFALL_MESSAGE =
  "There isn't enough equity to borrow at this mortgage balance and estimated property value.";

const equityCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

// First-mortgage balance cannot be more than twice the property value.
export const mortgageBalanceCeiling = propertyValue => {
  if (!Number.isFinite(propertyValue) || propertyValue <= 0) return null;
  return Math.floor(propertyValue * 2);
};

export const fieldHasAmount = input => {
  if (!input) return false;
  if (input.inputmask) {
    const raw = input.inputmask.unmaskedvalue();
    return raw !== undefined && raw !== null && String(raw) !== '';
  }
  return String(input.value ?? '').replace(/[^\d.-]/g, '') !== '';
};

export const syncEquityShortfallNotice = (cashInput, insufficient, details = {}) => {
  if (!cashInput) return;

  const group = cashInput.closest('.form-group') || cashInput.parentElement;
  if (!group) return;

  const {minimum = null, show = insufficient} = details;

  let notice = group.querySelector(':scope > .equity-hint');
  if (!notice) {
    notice = document.createElement('p');
    notice.className = 'equity-hint';
    notice.id = `${cashInput.id || 'cash-amount'}-equity-hint`;
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.hidden = true;
    group.appendChild(notice);
  }

  const parts = [];
  if (insufficient) parts.push(EQUITY_SHORTFALL_MESSAGE);
  if (Number.isFinite(minimum)) parts.push(`Minimum to borrow: ${equityCurrency.format(minimum)}.`);

  notice.textContent = parts.join(' ');
  notice.hidden = !show || parts.length === 0;
  notice.classList.toggle('is-visible', !notice.hidden);
  notice.classList.toggle('is-short', insufficient);
  cashInput.setAttribute('aria-invalid', insufficient ? 'true' : 'false');
  if (!notice.hidden) cashInput.setAttribute('aria-describedby', notice.id);

  const form = cashInput.closest('form');
  const pane = cashInput.closest('.tab-pane');
  [form, pane].filter(Boolean).forEach(node => {
    if (insufficient) node.dataset.insufficientEquity = 'true';
    else delete node.dataset.insufficientEquity;
  });

  const paneStep = Number(pane?.dataset?.step);
  const buttons = new Set([
    ...(pane ? pane.querySelectorAll('input[name="__next"]') : []),
    ...(form ? form.querySelectorAll('input[name="__next"]') : []),
    ...(pane ? pane.querySelectorAll('[data-action="changeStep"]') : []),
  ]);
  const widget = cashInput.closest('[data-prequal-form-embed], .widget-form_rate, .widget-wrapper');
  widget?.querySelectorAll('[data-calculate-btn]').forEach(btn => buttons.add(btn));

  buttons.forEach(btn => {
    const targetStep = Number(btn.dataset.step);
    const isForward =
      btn.name === '__next' ||
      btn.hasAttribute('data-calculate-btn') ||
      (!Number.isNaN(paneStep) && !Number.isNaN(targetStep) && targetStep > paneStep);
    if (!isForward) return;

    if (insufficient) {
      btn.disabled = true;
      btn.dataset.equityBlocked = 'true';
    } else if (btn.dataset.equityBlocked === 'true') {
      btn.disabled = false;
      delete btn.dataset.equityBlocked;
    }
  });
};

//==TomSelect

export const initTomMultiCheckboxSelect = (el, settings = {}) => {
  if (!el) return;

  const tomSettings = _.merge(
    {
      type: 'selectCheckboxes',
      maxItems: null,
      hideSelected: false,
      plugins: {
        checkbox_options: {
          checkedClassNames: ['ts-checked'],
          uncheckedClassNames: ['ts-unchecked'],
        },
        // 'remove_button': true,
        // 'clear_button': true
      },
      // render: {
      //   optgroup: function (data) {
      //     let optgroup = document.createElement('div');
      //     optgroup.className = 'ts-optgroup';
      //     optgroup.appendChild(data.options);
      //     return optgroup;
      //   },
      //   optgroup_header: function (data, escape) {
      //     const {value: key} = data;
      //     const options = Object.values(tom.options).filter(({class: _key, value}) => _key && _key === key);
      //     const selected = tom.items.filter((value) => options.find((item) => item.value == value));
      //     const checked = selected.length > 0 && selected.length === options.length;
      //
      //     const optgroupHeader = document.createElement('div');
      //     optgroupHeader.className = 'optgroup-header';
      //     optgroupHeader.innerHTML = `<label class="checkbox-control" for="${el.id}_${data.$order}"><input class="ts-optgroup-checkbox" type="checkbox" id="${el.id}_${data.$order}" ${checked ? 'checked' : ''}/><span></span></label>
      //                                         <div role="button" class="ts-optgroup-toggle">${escape(data.label)} ${selected.length > 0 ? `(${selected.length})` : ''}</div>`;
      //
      //     optgroupHeader.querySelector('.ts-optgroup-checkbox').addEventListener('change', (event) => {
      //       const {target} = event;
      //       const checked = target.checked;
      //       target.closest('.ts-optgroup').querySelectorAll('.option').forEach((el) => {
      //         if (checked && !el.classList.contains('selected') || !checked && el.classList.contains('selected')) {
      //           el.click();
      //         }
      //       });
      //     });
      //
      //     optgroupHeader.querySelector('.ts-optgroup-toggle').addEventListener('click', (event) => {
      //       const {target} = event;
      //       const collapsed = target.classList.contains('collapsed');
      //       target.classList[collapsed ? 'remove' : 'add']('collapsed');
      //       target.closest('.ts-optgroup').querySelectorAll('.option').forEach((el) => {
      //         el.classList[collapsed ? 'remove' : 'add']('d-none');
      //       });
      //     });
      //
      //     return optgroupHeader;
      //   },
      // }
    },
    settings
  );

  // const setOptgroupChecked = () => {
  //   for (const key of Object.keys(tom.optgroups)) {
  //     const group = tom.dropdown.querySelector(`[data-group="${key}"]`);
  //     if (group) {
  //       group.querySelector('.ts-optgroup-checkbox').checked = !group.querySelectorAll(`.option:not(.selected)`).length;
  //     }
  //   }
  // };

  const tom = initTomSelect(el, tomSettings);

  // el.addEventListener('change', () => {
  //   setOptgroupChecked();
  // });
  //
  // el.tomselect.on('dropdown_open', () => {
  //   setOptgroupChecked();
  // });

  return tom;
};

export const initTomSelect = (el, settings = {}) => {
  if (!el) return;

  el.setAttribute('autocomplete', 'off');
  el.classList.remove('form-control');
  el.classList.add('input-control');
  const wrapper =
    el.closest('.control-wrapper') || el.closest('.select-wrapper') || el.closest('.input-wrapper');
  const tooltipWrapper = wrapper.closest('.dropdown-filter') ? wrapper.closest('.dropdown-filter') : wrapper;

  let tom, bsTooltip, popper;

  settings = _.merge(
    {
      type: 'select',
      controlClass: 'ts-control',
      controlCustomClass: 'form-control',
      optionClass: 'dropdown-item',
      searchEnabled: el.multiple,
      dropdownSearchEnabled: false,
      noResultsTextEnabled: true,
      noResultsText: 'No results found for “{query}”',
      loadingMoreText: 'Loading more results...',
      dropdownInputPlaceholder: 'Search...',
      inputmask: null,
      autogrow: false,
      hasTags: false,
    },
    settings,
    JSON.parse(el.hasAttribute('data-ts-settings') && el.getAttribute('data-ts-settings')),
    ...Object.keys(el.dataset)
      .filter(key => key.indexOf('ts') > -1 && key !== 'tsSettings')
      .map(key => {
        const param = key.replace('ts', '');
        return {[param.replace(param[0], param[0].toLowerCase())]: el.dataset[key]};
      })
  );

  const actions = {
    setTooltipTitle: () => {
      const {tsTitle} = tooltipWrapper.dataset;
      const label =
        tsTitle + ' ' + tom.items.map(value => tom.options[value][tom.settings.labelField]).join(',');
      bsTooltip.setContent({'.tooltip-inner': label});
    },
    createTooltip: () => {
      return bootstrap.Tooltip.getOrCreateInstance(tooltipWrapper, {
        placement: 'bottom',
        trigger: 'manual',
        title: 'Title',
      });
    },
    handleShowDropdown: (dropdown, isShown) => {
      const changeEvent = new CustomEvent('toggle.tom.dropdown', {
        detail: {el, isShown},
        bubbles: true,
        cancelable: true,
      });
      dropdown.dispatchEvent(changeEvent);

      dropdown.classList[isShown ? 'add' : 'remove'](isShown ? 'active' : 'show');
      setTimeout(() => {
        dropdown.classList[isShown ? 'add' : 'remove'](isShown ? 'show' : 'active');
      }, 0);

      if (isShown) {
        if (bsTooltip) bsTooltip.hide();

        if (settings.dropdownPlacement) {
          dropdown.classList.add(...settings.dropdownPlacement.split(',').map(pos => `ts-placement-${pos}`));
        }

        if (tom.control_input.classList.contains('dropdown-input')) {
          if (settings.searchEnabled || settings.dropdownSearchEnabled) {
            if (settings.dropdownInputPlaceholder)
              tom.control_input.placeholder = settings.dropdownInputPlaceholder;
          } else {
            tom.control_input.closest('.dropdown-input-wrap').classList.add('d-none');
          }
        }
      }

      if (
        (!Array.from(dropdown.classList).includes('multi') &&
          el.closest('.modal') &&
          el.closest('.widget-form')) ||
        el.closest('.page-header')
      ) {
        dropdown.classList.add('ts-popper');
        const setDropdownWidth = () => {
          dropdown.style.width = dropdown.closest('.ts-wrapper').offsetWidth + 'px';
        };
        if (isShown) {
          setDropdownWidth(dropdown);
          window.addEventListener('resize', () => setDropdownWidth(dropdown), {passive: true});
          popper = Popper.createPopper(dropdown.closest('.ts-wrapper'), dropdown, {
            placement: 'bottom-start',
            strategy: 'fixed',
          });
        } else {
          window.removeEventListener('resize', () => setDropdownWidth(dropdown));
          popper.destroy();
        }
      }
    },
  };

  const init = () => {
    const render = {
      option: (data, escape) => {
        const label = escape(data[settings.labelField || 'text']);
        if (data.svgIcon) {
          return `<div class="dropdown-item d-flex align-items-center" data-item-class-name="text-uppercase">
                            <svg class="svg-icon text-primary flex-shrink-0 me-2"><use xlink:href="../images/spritemap/spritemap.svg#${escape(data.svgIcon)}"></use></svg>
                            <span class="text">${label}</span>
                        </div>`;
        }
        return `<div><span class="text">${label}</span></div>`;
      },
      no_results: (data, escape) => {
        if (!settings.noResultsTextEnabled) return null;
        const text = settings.noResultsText.replaceAll('{query}', '<b>' + escape(data.input) + '</b>');
        return `<div class="no-results">${text}</div>`;
      },
      loading_more: () => {
        return `<div class="loading-more-results d-flex align-items-center"><div class="spinner"></div>${settings.loadingMoreText}</div>`;
      },
      option_create: function (data, escape) {
        if (el.classList.contains('has-create'))
          tom.dropdown.classList[
            tom.dropdown.querySelectorAll('.dropdown-item').length > 0 ? 'remove' : 'add'
          ]('only-create-option');
        return '<div class="create d-none">Add <strong>' + escape(data.input) + '</strong>&hellip;</div>';
      },
      loading: function () {
        return `<div class="spinner-block"><div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div></div>`;
      },
    };

    const plugins = {
      ...(!settings.searchEnabled ? {clear_button: false} : {}),
      ...(settings.autogrow ? {input_autogrow: true} : {}),
      ...(settings.dropdownSearchEnabled ? {dropdown_input: true} : {}),
    };

    const defaultSettings = {
      create: false,
      maxItems: el.multiple ? null : 1,
      maxOptions: null,
      render: {
        ...render,
        item: render.option,
      },
      shouldLoad: query => {
        const resultItems = tom.currentResults.items;
        el.nextElementSibling.classList[resultItems.length > 0 ? 'remove' : 'add']('no-results');
        return query;
      },
      onItemAdd: (value, $item) => {
        if (!el.tomselect.options[value].hasOwnProperty('$div')) {
          $item.classList.add('value-created');
        }
        if (el.tomselect.options[value].hasOwnProperty('$option')) {
          const itemText = el.tomselect.options[value].$option.dataset.itemText;
          const itemClassName = el.tomselect.options[value].$option.dataset.itemClassName;
          if (itemText) $item.innerHTML = escapeHtml(itemText);
          if (itemClassName) $item.classList.add(itemClassName);
        }
      },
      onChange: value => {
        if (bsTooltip) actions.setTooltipTitle();
        if (wrapper) wrapper.classList[value ? 'add' : 'remove']('not-empty');
      },
      onInitialize() {
        const wrapper = el.closest('.control-wrapper');
        const isSearchControl = wrapper?.classList.contains('search-input-wrapper');
        if (settings.controlCustomClass) {
          this.control.classList.add(...settings.controlCustomClass.trim().split(/\s+/));
        }
        if (settings.inputmask || isSearchControl) {
          const inputs = wrapper.querySelectorAll('.ts-control input, .dropdown-input-wrap input');
          inputs.forEach(el => {
            if (settings.inputmask) initInputMaskByKey(el, settings.inputmask);
            if (isSearchControl) initSearchControl(el);
          });
        }
      },
      onType: str => {
        if (settings.type === 'typeahead' && !str.length) tom.close();
      },
      onDropdownOpen(dropdown) {
        dropdown.classList.add('dropdown-menu');
        actions.handleShowDropdown(dropdown, true);
      },
      onDropdownClose(dropdown) {
        actions.handleShowDropdown(dropdown, false);
      },
      plugins,
    };

    const tomSettings = _.merge(defaultSettings, settings);

    for (const plugin in tomSettings.plugins) {
      if (tomSettings.plugins.hasOwnProperty(plugin) && tomSettings.plugins[plugin] === false) {
        delete tomSettings.plugins[plugin];
      }
    }

    if (tomSettings.hasTags) el.classList.add('has-tags');
    if (tomSettings.create) el.classList.add('has-create');
    if (!tomSettings.searchEnabled) el.classList.add('no-search');
    if (tomSettings.dropdownExpanded) el.classList.add('ts-wrapper-expanded');

    if (tomSettings.searchEnabled || tomSettings.dropdownSearchEnabled) el.classList.add('has-search');
    else tomSettings.controlInput = '<input readonly class="ts-custom-placeholder"/>';

    return new TomSelect(el, tomSettings);
  };

  tom = init();

  bsTooltip =
    tooltipWrapper.hasAttribute('data-ts-toggle') &&
    tooltipWrapper.getAttribute('data-ts-toggle') === 'tooltip' &&
    actions.createTooltip();
  if (bsTooltip) {
    if (tom.items.length) actions.setTooltipTitle();
    tooltipWrapper.addEventListener('mouseenter', () => {
      const isShown =
        tom.items.length > 0 &&
        !tooltipWrapper.querySelector('.dropdown-active') &&
        (isMatchMedia('desktop') || tooltipWrapper.classList.contains('select-btn-icon'));
      if (isShown) bsTooltip.show();
    });
    tooltipWrapper.addEventListener('mouseleave', () => {
      bsTooltip.hide();
    });
  }

  return tom;
};

export const initTypeahead = (el, settings = {}) => {
  if (!el) return;
  return initTomSelect(
    el,
    _.merge(
      {
        type: 'typeahead',
        searchEnabled: true,
        noResultsTextEnabled: false,
        create: false,
        createOnBlur: true,
        openOnFocus: false,
        hideSelected: true,
        persist: false,
        maxItems: 1,
        maxOptions: 20,
        // plugins: {
        //     'clear_button': true
        // }
      },
      settings
    )
  );
};

export const initSearchControl = el => {
  if (!el) return;

  const wrapper = el.closest('.search-input-wrapper');
  if (!wrapper) return;

  wrapper.querySelector('.reset-button').addEventListener('click', () => {
    if (el.tomselect) {
      el.tomselect.setValue(null);
    } else {
      el.value = null;
      el.dispatchEvent(new Event('change'));
    }
  });
};

//== intl-tel-input

export function initIntlTelInput(input) {
  if (!input || input.iti) return;

  const {initialCountry = 'us', preferredCountries} = input.dataset;

  const defaultPlaceholder = input.dataset.placeholder;
  const getDropdownContainer = () => {
    if (input.closest('.modal')) return input.closest('.modal');
    if (input.closest('.widget-offcanvas')) return input.closest('.widget-offcanvas');
    return document.body;
  };
  let maskInstance = null;

  input.dataset.currentCountry = initialCountry;

  const iti = intlTelInput(input, {
    initialCountry,
    dropdownContainer: getDropdownContainer(),
    separateDialCode: true,
    placeholderNumberType: 'MOBILE',
    nationalMode: true,
    preferredCountries: preferredCountries ? preferredCountries.split(',').map(c => c.trim()) : [],
    loadUtils: () => import('intl-tel-input/utils'),
  });

  function applyMask() {
    // Inputmask blocks text entry on Edge/Windows when OS text suggestions are on.
    // Skip masking there rather than lock users out of the field.
    if (isWindowsEdge()) return;

    const placeholder = input.getAttribute('placeholder');

    if (!placeholder || placeholder === defaultPlaceholder) return;

    input.placeholder = defaultPlaceholder;

    const currentValue = input.value;

    if (maskInstance) maskInstance.remove();

    maskInstance = new Inputmask({
      mask: placeholder.replace(/\d/g, '9'),
      placeholder: '_',
      showMaskOnHover: true,
      showMaskOnFocus: true,
      clearIncomplete: true,
    });

    maskInstance.mask(input);

    if (currentValue) {
      input.value = currentValue;
      maskInstance.setValue(currentValue);
    }
  }

  iti.promise.then(applyMask);

  // Preserve partially-typed digits on blur (clearIncomplete would otherwise wipe them)
  let _blurDigits = '';
  input.addEventListener(
    'blur',
    () => {
      _blurDigits = input.value.replace(/\D/g, '');
    },
    {capture: true}
  );
  input.addEventListener('blur', () => {
    setTimeout(() => {
      if (!input.value.replace(/\D/g, '') && _blurDigits.length > 0) {
        input.value = _blurDigits;
        input.dispatchEvent(new Event('input', {bubbles: true}));
      }
    }, 0);
  });

  input.addEventListener('countrychange', () => {
    const newCountry = iti.getSelectedCountryData().iso2;
    if (input.dataset.currentCountry !== newCountry) {
      input.dataset.currentCountry = newCountry;
      applyMask();
    }
  });

  return iti;
}

//==RangeSlider

export const initRangeSlider = (sliderEl, options = {}) => {
  if (!sliderEl) return;

  const wrapper = sliderEl.closest('.range-wrapper');

  if (!wrapper) return;

  const input = wrapper.querySelector('.range-control');
  const inputFormat = wrapper.querySelector('.range-format');
  const rangeMin = wrapper.querySelector('.range-min');
  const rangeMax = wrapper.querySelector('.range-max');

  const formatter = wNumb({
    decimals: 0,
    thousand: ',',
    prefix: '$',
  });

  const value = parseFloat(input.value) || 0;
  const step = parseFloat(input.step);
  const range = {
    min: parseFloat(input.min),
    max: parseFloat(input.max),
  };

  if (rangeMin) rangeMin.textContent = formatter.to(range.min);
  if (rangeMax) rangeMax.textContent = formatter.to(range.max);

  noUiSlider.create(sliderEl, {
    start: [value],
    step,
    range,
    format: formatter,
    connect: [true, false],
    ...options,
  });

  sliderEl.noUiSlider.on('update', function (values, handle) {
    if (inputFormat) inputFormat.value = values[handle];
    input.value = formatter.from(values[handle]);
  });

  input.addEventListener('change', function () {
    const raw = formatter.from(this.value);
    sliderEl.noUiSlider.set(raw);
  });

  inputFormat &&
    inputFormat.addEventListener('change', function () {
      sliderEl.noUiSlider.set(this.value);
    });
};

//==initFormElements

export const initFormElements = wrapper => {
  wrapper = wrapper || document;

  wrapper.querySelectorAll('.dropdown-select').forEach(el => {
    initDropdownSelect(el);
  });

  wrapper.querySelectorAll('input[data-mask]:not(.js-input)').forEach(el => {
    initInputMaskByKey(el, el.getAttribute('data-mask'));
  });

  wrapper.querySelectorAll('select.js-select').forEach(el => {
    if (el.multiple) initTomMultiCheckboxSelect(el);
    else initTomSelect(el);
  });

  wrapper.querySelectorAll('.js-intlTel').forEach(input => {
    initIntlTelInput(input);
  });

  wrapper.querySelectorAll('.search-input-wrapper input.form-control').forEach(el => {
    initSearchControl(el);
  });

  wrapper.querySelectorAll('.js-range-slider').forEach(el => {
    initRangeSlider(el);
  });

  wrapper.querySelectorAll('[data-bs-toggle="changeDepends"]').forEach(depEl => {
    const {bsTarget} = depEl.dataset;
    const targets = document.querySelectorAll(bsTarget);
    if (!targets.length) return;

    const isSameValue = (currentValue, newValue) => {
      if (Array.isArray(currentValue) && Array.isArray(newValue)) {
        return (
          currentValue.length === newValue.length &&
          currentValue.every((item, index) => item === newValue[index])
        );
      }
      if (Array.isArray(currentValue)) {
        return currentValue.length === 1 && currentValue[0] === newValue;
      }
      if (Array.isArray(newValue)) {
        return newValue.length === 1 && currentValue === newValue[0];
      }
      return currentValue === newValue;
    };

    const setTargetValue = (target, value) => {
      if (target.tomselect) {
        const currentValue = target.tomselect.getValue();
        if (!isSameValue(currentValue, value)) {
          target.tomselect.setValue(value);
        }
      } else if (target.matches('select')) {
        if (target.value !== value) {
          target.value = value;
          target.dispatchEvent(new Event('change'));
        }
      } else if (target.classList.contains('radio-group')) {
        const radio = target.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change'));
        }
      } else {
        target.value = value;
      }
    };

    if (depEl.classList.contains('radio-group')) {
      const radios = depEl.querySelectorAll('input[type="radio"][name]');
      if (!radios.length) return;

      const handleRadioChange = () => {
        const checked = depEl.querySelector('input[type="radio"]:checked');
        if (!checked) return;
        const value = checked.value;
        targets.forEach(target => {
          if (target.value !== value) setTargetValue(target, value);
        });
      };

      radios.forEach(radio => radio.addEventListener('change', handleRadioChange));
    } else {
      depEl.addEventListener('change', () => {
        const value = depEl.value;
        targets.forEach(target => setTargetValue(target, value));
      });
    }
  });

  const toggleRadios = wrapper.querySelectorAll('input[type="radio"][data-toggle-target]');

  toggleRadios.forEach(radio => {
    radio.addEventListener('change', updateVisibility);
  });

  function updateVisibility() {
    const groups = {};

    toggleRadios.forEach(radio => {
      const name = radio.name;
      if (!groups[name]) groups[name] = [];
      groups[name].push(radio);
    });

    Object.values(groups).forEach(radios => {
      radios.forEach(radio => {
        const targetSelector = radio.dataset.toggleTarget;
        const target = document.querySelector(targetSelector);
        if (!target) return;

        if (radio.checked && radio.value === 'true') {
          target.classList.remove('d-none');
        } else if (radio.checked && radio.value === 'false') {
          target.classList.add('d-none');
        }
      });
    });
  }
};

export const getFieldValue = field => {
  if (!field) return null;

  if (field.closest('.d-none, [hidden], [aria-hidden="true"]')) return null;

  const type = (field.getAttribute('type') || '').toLowerCase();

  if (field.inputmask) {
    const unmasked = field.inputmask.unmaskedvalue()?.trim() ?? '';
    if (!unmasked) return null;
    if (field.inputmask.isComplete && !field.inputmask.isComplete()) return null;
    return field.value.trim();
  }

  if (field.iti) {
    const number = field.iti.getNumber();
    return number && field.iti.isValidNumber() ? number : field.value;
  }

  if (type === 'file') return field.files?.length ? field.files : null;

  if (type === 'radio' || type === 'checkbox') {
    const form = field.closest('form');
    const name = field.name;
    if (!name) return field.checked ? field.value : null;

    const inputs = [...form.querySelectorAll(`input[name="${CSS.escape(name)}"][type="${type}"]`)];
    if (type === 'radio') {
      const checked = inputs.find(el => el.checked);
      return checked ? checked.value : null;
    }
    if (type === 'checkbox') {
      const values = inputs.filter(el => el.checked).map(el => el.value);
      return values.length ? values : null;
    }
  }

  if (field.tomselect) {
    const value = field.tomselect.getValue();
    return Array.isArray(value) ? (value.length ? value : null) : value || null;
  }

  let value = field.value?.trim?.() ?? '';
  if (value === '') return null;

  return value;
};

export const validateField = field => {
  if (!field) return false;

  const wrapper = field.closest('.control-wrapper');

  if (field.closest('.d-none, [hidden], [aria-hidden="true"]')) {
    if (wrapper) wrapper.classList.remove('is-invalid');
    return true;
  }

  const type = (field.getAttribute('type') || '').toLowerCase();
  let valid = true;

  if (type === 'email' || field.dataset?.mask === 'email') {
    valid = validateEmailField(field);
  } else if (field.required) {
    const v = getFieldValue(field);
    valid = !(v == null || v === '' || (Array.isArray(v) && !v.length));
  }

  const touched = field.dataset.touched === 'true';

  if (wrapper) {
    if (!valid && touched) {
      wrapper.classList.add('is-invalid');
    } else {
      wrapper.classList.remove('is-invalid');
    }
  }

  return valid;
};

export const validateEmailField = field => {
  if (!field) return;
  const value = getFieldValue(field);
  return !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

export const resetField = field => {
  if (!field) return;

  const type = field.getAttribute('type')?.toLowerCase();
  let defaultValue = field.dataset.defaultValue ?? null;

  if (type && /radio|checkbox/.test(type)) {
    const form = field.closest('form');
    const name = field.getAttribute('name');
    const group = field.closest('.radio-group, .checkbox-group');

    if (group?.dataset?.defaultValue) defaultValue = group.dataset.defaultValue;

    if (form && name) {
      form.querySelectorAll(`input[type="${type}"][name="${CSS.escape(name)}"]`).forEach((el, i) => {
        const shouldCheck =
          (defaultValue && el.value === defaultValue) || (type === 'radio' && i === 0 && !defaultValue);
        el.checked = !!shouldCheck;
      });
    } else {
      field.checked = false;
    }
  } else {
    if (field.tomselect) field.tomselect.setValue(defaultValue);
    else field.value = defaultValue ?? '';
  }

  field.dispatchEvent(new Event('change', {bubbles: true}));
};

export const toggleHighlightField = (field, isValid) => {
  const wrapper = field.closest('.control-wrapper');
  if (!wrapper) return;
  wrapper.classList.toggle('is-invalid', !isValid);
};

//== COPY

export function copyTextToClipboard(target, text, successText = 'Copied!') {
  function fallbackCopyTextToClipboard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (!successful) {
        console.warn('Fallback: Copy command was unsuccessful');
      }
    } catch (err) {
      console.error('Fallback: Unable to copy text', err);
    }

    document.body.removeChild(textArea);
  }

  function showCopySuccess() {
    const bsTooltip = bootstrap.Tooltip.getOrCreateInstance(target, {
      trigger: 'manual',
      placement: 'top',
      title: successText,
    });
    bsTooltip.show();

    setTimeout(() => {
      bsTooltip.hide();
    }, 1500);
  }

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showCopySuccess();
      })
      .catch(err => {
        console.error('Async: Could not copy text:', err);
        fallbackCopyTextToClipboard(text);
      });
  } else {
    fallbackCopyTextToClipboard(text);
  }
}

// == Spinner

export const getSpinner = (type = 'dark', wrapper) => {
  const spinner = document.createElement('div');
  spinner.classList.add('spinner-container');
  spinner.innerHTML = `<div class="spinner-border text-${type}" role="status">
                            <span class="visually-hidden">Loading...</span>
                         </div>`;
  wrapper.appendChild(spinner);
  return spinner;
};

export const showLoading = (wrapper = document.body, type = 'dark') => {
  if (window.embedLoading) return;
  window.embedLoading = getSpinner(type, wrapper);
  wrapper.append(window.embedLoading);
};

export const hideLoading = () => {
  window.embedLoading && window.embedLoading.remove();
  window.embedLoading = null;
};

export function showWidgetError(widget, message, append = true) {
  if (!widget) return;

  if (append) widget.style.position ||= 'relative';

  let overlay = document.createElement('div');
  overlay.className = 'widget-error-overlay';
  overlay.textContent = message;

  if (append) {
    widget.style.position ||= '';
    overlay.classList.add('appended');
  } else {
    widget.style.position ||= 'relative';
    overlay.classList.remove('appended');
  }

  widget.appendChild(overlay);
}

export function hideWidgetError() {
  let overlay = document.querySelector('.widget-error-overlay')?.remove();
}

export function getQueryParam(key, url = window.location.href) {
  const params = new URL(url).searchParams;
  return params.get(key);
}

export function getCookie(name) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : '';
}

// HubSpot visitor token (carries the GCLID for ad attribution). Same lookup order HubSpot's own
// form script uses: the tracking cookie first, then the query-string fallbacks.
export function getHubSpotUtk() {
  return (
    getCookie('hubspotutk') ||
    getQueryParam('hutk') ||
    getQueryParam('hubspotutk') ||
    getQueryParam('utm_hutk') ||
    ''
  );
}

export function setQueryParam(key, value, {replace = true} = {}) {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);

  if (replace) {
    window.history.replaceState({}, '', url);
  } else {
    window.history.pushState({}, '', url);
  }
}

export function removeQueryParam(key, {replace = true} = {}) {
  const url = new URL(window.location.href);
  url.searchParams.delete(key);

  if (replace) {
    window.history.replaceState({}, '', url);
  } else {
    window.history.pushState({}, '', url);
  }
}
